import { Popup } from "../../../SharedComponents/Popup";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import { ErrorText, WarningText, SuccessText } from "../../ui/StatusText";
import { FileUploadField } from "../../ui/FileUploadField";
import { useBankDetailsForm } from "../../services/hooks/useBankDetailsForm";
import { getUserId } from "../../../../utils/auth";
import { ACCOUNT_TYPES, BANK_STATUS_MESSAGES } from "../../services/utils/constants";
import styles from "./style.module.css";

export const EditBankDetailsModal = ({
  isOpen,
  onClose,
  currentDetails = {},
  onSaveSuccess,
}) => {
  const userId = getUserId();
  const bank = useBankDetailsForm({ userId, currentDetails, isOpen });
  const { state, loading, derived, handlers, mutators } = bank;

  if (!isOpen) return null;

  return (
    <Popup onClose={onClose} contentClassName={styles.modalContent} hideCloseButton>
      <div className={styles.stickyHeader}>
        <span className={styles.title}>Bank details</span>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <SvgIcons name="cross_btn" width="20" height="20" style={{ color: "#999" }} />
        </button>
      </div>
      <div className={styles.formBody}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Account holder name <span>*</span>
          </label>
          <input
            disabled={loading.isSaving}
            className={`${styles.input} ${state.errors.accountHolderName ? styles.inputError : ""}`}
            value={state.accountHolderName}
            onChange={(e) => handlers.setAccountHolderName(e.target.value)}
            placeholder="As per bank records"
          />
          {state.errors.accountHolderName && <ErrorText>{state.errors.accountHolderName}</ErrorText>}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Mobile number <span>*</span>
          </label>
          <input
            disabled={loading.isSaving}
            className={`${styles.input} ${state.errors.mobileNumber ? styles.inputError : ""}`}
            value={state.mobileNumber}
            onChange={(e) => handlers.setMobileNumber(e.target.value)}
            placeholder="Enter 10-digit mobile number"
            inputMode="numeric"
            maxLength={10}
          />
          {state.errors.mobileNumber && <ErrorText>{state.errors.mobileNumber}</ErrorText>}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            IFSC code <span>*</span>
          </label>
          <input
            disabled={loading.isSaving}
            className={`${styles.input} ${state.errors.ifscCode || derived.showIfscFormatError || state.ifscStatus === "failed" ? styles.inputError : ""
              }`}
            value={state.ifscCode}
            onChange={(e) => handlers.setIfscCode(e.target.value)}
            placeholder="HDFC0001234"
            maxLength={11}
          />
          {derived.showIfscFormatError && <ErrorText>Invalid IFSC code</ErrorText>}
          {!derived.showIfscFormatError && state.errors.ifscCode && <ErrorText>{state.errors.ifscCode}</ErrorText>}
          {!derived.showIfscFormatError && !state.errors.ifscCode && state.ifscStatus === "verifying" && (
            <span className={styles.verifyingText}>Verifying IFSC code...</span>
          )}
          {!derived.showIfscFormatError && !state.errors.ifscCode && state.ifscStatus === "success" && (
            <SuccessText>IFSC verified successfully</SuccessText>
          )}
          {!derived.showIfscFormatError && !state.errors.ifscCode && state.ifscStatus === "failed" && (
            <ErrorText>{state.ifscError || "Invalid IFSC code"}</ErrorText>
          )}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Bank name <span>*</span>
          </label>
          <input
            disabled
            readOnly
            className={styles.disabledInput}
            value={state.bankName}
            placeholder="Please enter valid IFSC code"
          />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Branch name <span>*</span>
          </label>
          <input
            disabled
            readOnly
            className={styles.disabledInput}
            value={state.branchName}
            placeholder="Please enter valid IFSC code"
          />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Account number <span>*</span>
          </label>
          <input
            disabled={loading.isSaving}
            className={`${styles.input} ${state.errors.accountNumber ? styles.inputError : ""}`}
            value={state.accountNumber}
            onChange={(e) => handlers.setAccountNumber(e.target.value)}
            placeholder="Enter account number"
            inputMode="numeric"
          />
          {state.errors.accountNumber && <ErrorText>{state.errors.accountNumber}</ErrorText>}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Re-enter Account number <span>*</span>
          </label>
          <input
            disabled={loading.isSaving}
            className={`${styles.input} ${state.errors.confirmAccountNumber || derived.accountNumbersMismatch ? styles.inputError : ""
              }`}
            value={state.confirmAccountNumber}
            onChange={(e) => handlers.setConfirmAccountNumber(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
            placeholder="Re-enter account number"
            inputMode="numeric"
          />
          {derived.accountNumbersMismatch && <ErrorText>Account numbers do not match</ErrorText>}
          {!derived.accountNumbersMismatch && state.errors.confirmAccountNumber && (
            <ErrorText>{state.errors.confirmAccountNumber}</ErrorText>
          )}
          {derived.accountNumbersMatch && <SuccessText>Account verified successfully</SuccessText>}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Account type <span>*</span>
          </label>
          <div className={styles.toggleRow}>
            {ACCOUNT_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                disabled={loading.isSaving}
                className={`${styles.toggleBtn} ${state.accountType === type.value ? styles.toggleBtnActive : ""}`}
                onClick={() => handlers.setAccountType(type.value)}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Cancelled cheque or Passbook front page <span>*</span>
          </label>
          <FileUploadField
            inputRef={state.chequeInputRef}
            file={state.chequeFile}
            existingUrl={state.existingBankProofUrl}
            existingName={state.existingBankProofName}
            onView={handlers.viewDocument}
            onSelect={handlers.handleChequeFileSelect}
            accept=".jpg,.jpeg,.png,.pdf"
            hint="JPG, PNG or PDF"
            error={state.errors.chequeFile}
            disabled={loading.isSaving}
          />
          {state.errors.chequeFile && <ErrorText>{state.errors.chequeFile}</ErrorText>}
          {loading.isSaving && <span className={styles.verifyingText}>Verifying your bank details...</span>}
          {!loading.isSaving && state.verificationStatus === "verified" && (
            <SuccessText>Bank details verified successfully</SuccessText>
          )}
          {!loading.isSaving && state.verificationStatus === "failed" && (
            <ErrorText>{BANK_STATUS_MESSAGES[state.verificationStatus]}</ErrorText>
          )}
          {!loading.isSaving &&
            BANK_STATUS_MESSAGES[state.verificationStatus] &&
            state.verificationStatus !== "failed" && (
              <WarningText>{BANK_STATUS_MESSAGES[state.verificationStatus]}</WarningText>
            )}
          {state.apiError && (
            <div className={styles.apiErrorRow}>
              <SvgIcons name="error_circle_icon" width="16" height="16" className={styles.apiErrorIcon} />
              <span className={styles.apiErrorText}>{state.apiError}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.stickyFooter}>
        <button
          className={styles.saveBtn}
          onClick={() => mutators.handleSave({ onSaveSuccess, onClose })}
          disabled={loading.isSaving || !derived.isFormComplete}
        >
          {loading.isSaving && <span className={styles.spinner} />}
          Save details
        </button>
      </div>
    </Popup>
  );
};
